import _ from "lodash";
import React from "react";
import * as XLSX from "xlsx";
import * as CO from "./commons";
import * as echarts from "echarts";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import { TB, TC, T, LANG } from "../../../Constants";

type BarChartProps = {
    /** The palette of color to user */
    palette: string[];
    /** Is the component refreshing */
    refreshing?: boolean;
    /** The energy type */
    energy?: string;
    /** The color of the selected segment */
    color?: string;
    /** A callback to refresh the data of the component */
    refresh: (params?: Parameters<T.API.Report.Dashboard.GetESGSectionData>[0]["other"]) => void;
    /** The unit of the data */
    unit: string;
    /** The data to display */
    data: ReturnType<T.API.Report.Dashboard.GetESGSectionData>["bar"];
};

export type BarChartRef = {
    /** Should the data be smoothed */
    smoothed: boolean;
};

/** Map each abbreviated month to its index */
const month_indexes = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

const BarChart = React.forwardRef<BarChartRef, BarChartProps>(({ refresh, ...props }, ref) => {
    const lg = H.useLanguage();
    const smoothing = H.useBoolean(true);
    const show_both_years = H.useBoolean(false);
    const chartRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => ({ smoothed: smoothing.value }), [smoothing.value]);
    const local_lang_value = React.useMemo(() => LANG.getHtmlLanguageCode(lg.prop), [lg.prop]);

    const getFullMonthName = React.useCallback((abbreviatedMonth: string) => {
        const month_index = month_indexes.findIndex(m => m === abbreviatedMonth);
        if (month_index < 0) return "N/A";
        else return new Date(2024, month_index, 1).toLocaleString(local_lang_value, { month: 'long' });
    }, [local_lang_value]);

    const excel_export = React.useCallback(() => {
        if (props.data) {
            const data = props.data.map(d => ({
                [lg.getStaticText(TC.DASH_ENV_CONFIG_GROUPING)]: d.segment,
                [lg.getStaticText(TC.DATASET_COL_TIME)]: d.time,
                [lg.getStaticText(TC.DASH_ENV_VALUE_CURRENT_YEAR)]: TB.formatNumber(d.sumvalue, local_lang_value),
                [lg.getStaticText(TC.DASH_ENV_VALUE_PREVIOUS_YEAR)]: TB.formatNumber(d.ytd_sumvalue, local_lang_value)
            })).sort((a, b) => TB.sortDates(a[lg.getStaticText(TC.DATASET_COL_TIME)], b[lg.getStaticText(TC.DATASET_COL_TIME)]));
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "sheet1");
            XLSX.writeFile(wb, "BarChart.xlsx");
        }
    }, [props.data]);

    const formatted_dates = React.useMemo(() => {
        const dates = props.data.map(d => d.time);
        const sorted_uniques_dates = _.uniq(dates).sort(TB.sortDates);
        return sorted_uniques_dates.map(time => new Date(time).toLocaleString("en-UK", { month: "short" }));
    }, [props.data]);

    const series = React.useMemo(() => {
        //Then seperate the value and ytd_value by segment
        const segment_data: Record<string, BarChartProps["data"]> = {};
        const segment_ytd_data: Record<string, BarChartProps["data"]> = {};

        props.data.forEach(d => {
            if (!Array.isArray(segment_data[d.segment])) segment_data[d.segment] = [];
            if (!Array.isArray(segment_ytd_data[d.segment])) segment_ytd_data[d.segment] = [];

            segment_data[d.segment].push(d);
            segment_ytd_data[d.segment].push(d);
        });

        // Calculate the sum of values for each segment
        const segmentSums = Object.entries(segment_data).map(([segment, data]) => {
            const sum = data.reduce((acc, d) => acc + (d.sumvalue || 0), 0);
            return { segment, sum };
        });
        // Sort segments by the sum of their values
        segmentSums.sort((a, b) => b.sum - a.sum);

        // Create a sorted segmentData object based on the sorted segmentSums
        const sortedSegmentData: typeof segment_data = {};
        segmentSums.forEach(elem => sortedSegmentData[elem.segment] = segment_data[elem.segment]);

        // Sort the each element in sortedSegmentData segment by time
        Object.entries(sortedSegmentData).forEach(([key, segment]) => {
            segment.sort((a, b) => TB.sortDates(a.time, b.time));
        });

        // Create a sorted segmentData object based on the sorted segmentSums
        const sortedYtdSegmentData: typeof segment_ytd_data = {};
        segmentSums.forEach(elem => sortedYtdSegmentData[elem.segment] = segment_ytd_data[elem.segment]);

        // Sort the each element in sortedYtdSegmentData segment by time
        Object.entries(sortedYtdSegmentData).forEach(([key, segment]) => {
            segment.sort((a, b) => TB.sortDates(a.time, b.time));
        });

        const series_data = [];

        if (show_both_years.value) Object.entries(sortedYtdSegmentData).forEach(([key, segment], index) => {
            const valueData = segment.map((d) => d.ytd_sumvalue);
            series_data.push({
                type: "bar",
                data: valueData,
                stack: "last year",
                name: lg.getStaticText(TC.NRJ_DASH_LAST_YEAR) + ' ' + key,
                itemStyle: { color: TB.lightenColor(props.color || props.palette[index], 60) },
            });
        });

        Object.entries(sortedSegmentData).forEach(([key, segment], index) => {
            const valueData = segment.map((d) => d.sumvalue);
            series_data.push({
                type: "bar",
                data: valueData,
                stack: "this year",
                itemStyle: { color: props.color || props.palette[index] },
                name: lg.getStaticText(TC.NRJ_DASH_CURRENT_YEAR) + ' ' + key,
            });
        });

        return series_data;
    }, [lg, props.data, props.palette, show_both_years.value]);

    const chart_options = React.useMemo<echarts.EChartsOption>(() => ({
        series: series,
        yAxis: { type: "value" },
        legend: { type: 'scroll', width: '75%', left: 0 },
        xAxis: { type: "category", data: formatted_dates },
        grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            formatter: params => {
                const date = getFullMonthName(params[0].axisValue);
                let content = `<strong>${date}</strong><br />`;
                params.forEach(param => {
                    if (param.value) content += `${param.marker}<strong>${param.seriesName}</strong>: ${TB.formatNumber(param.value, local_lang_value)} ${props.unit}<br />`;
                });
                return content;
            }
        },
        toolbox: {
            width: "20%",
            feature: {
                // dataView: { readOnly: false },
                saveAsImage: { title: lg.getStaticText(TC.NRJ_DASH_SAVE_IMAGE), },
                myTool2: { show: true, icon: CO.icons.excel, onclick: excel_export, title: lg.getStaticText(TC.JS_PORTFOLIO_EXPORT) },
                // myTool1: { show: true, icon: CO.icons.refresh, title: lg.getStaticText(TC.GLOBAL_REFRESH), onclick: () => refresh({ is_spread: smoothing.value }) },
            }
        },
    }), [lg, series, formatted_dates, smoothing.value, props.unit, excel_export, refresh, getFullMonthName, props.color]);

    React.useEffect(() => {
        if (chartRef.current) {
            const chartInstance = echarts.init(chartRef.current);
            chartInstance.setOption(chart_options);
            return () => chartInstance?.dispose?.();
        }
    }, [chart_options]);

    const toggle_smoothing = React.useCallback(() => {
        // Refresh the data with the new smoothing value
        refresh({ is_spread: !smoothing.value });
        // Toggle the smoothing flag
        smoothing.toggle();
    }, [refresh, smoothing]);

    const labels = React.useMemo(() => ({
        years: show_both_years.value ? TC.NRJ_DASH_SHOW_CURRENT_YEAR : TC.NRJ_DASH_SHOW_B_YEARS,
        smoothing: smoothing.value ? TC.NRJ_DASH_DATA_RAW : TC.NRJ_DASH_DATA_SMOOTHING,
    }), [show_both_years.value, smoothing.value]);

    return <div className='esg-graph d-flex flex-column w-100 h-100'>
        <C.Spinner loading={props.refreshing} min_load_size="100%">
            <div className='flex-grow-1 w-100'>
                <div ref={chartRef} className='h-100' />
            </div>

            <C.Flex direction="row" justifyContent="between" className="mt-2">
                <C.Button size="sm" icon="calendar" text={labels.years} onClick={show_both_years.toggle} />
                <C.Button size="sm" icon="chart-line" text={labels.smoothing} onClick={toggle_smoothing} />
            </C.Flex>
        </C.Spinner>
    </div>;
});


BarChart.displayName = "BarChart";
export default BarChart;